import { Link, Navigate } from "react-router-dom";
import {
  INSTALL_CLI,
  DOCS_RADIO,
  PYPI_PACKAGE,
  WHEEL_URL,
} from "@/lib/agentKit";
import {
  GITHUB_OSS,
  RAILWAY_DEPLOY,
  RAILWAY_TEMPLATE_URL,
} from "@/lib/hosts";

function H1({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="mb-4 text-3xl font-semibold tracking-tight text-white">
      {children}
    </h1>
  );
}
function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-10 text-xl font-semibold text-white">{children}</h2>
  );
}
function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-4 text-[15px] leading-relaxed text-zinc-400">{children}</p>
  );
}
function Pre({ children }: { children: string }) {
  return (
    <pre className="mb-6 overflow-x-auto rounded-xl border border-white/10 bg-zinc-950/80 p-4 font-mono text-[12px] leading-relaxed text-zinc-200">
      {children}
    </pre>
  );
}
function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[13px] text-orange-200/90">
      {children}
    </code>
  );
}
function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="mb-6 list-disc space-y-2 pl-5 text-[15px] text-zinc-400">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
function Callout({
  title,
  children,
  tone = "default",
}: {
  title: string;
  children: React.ReactNode;
  tone?: "default" | "saas" | "oss" | "railway";
}) {
  const border =
    tone === "saas"
      ? "border-orange-500/35 bg-orange-500/5"
      : tone === "oss"
        ? "border-sky-500/30 bg-sky-500/5"
        : tone === "railway"
          ? "border-violet-500/30 bg-violet-500/5"
          : "border-white/10 bg-white/[0.03]";
  return (
    <div className={`mb-6 rounded-2xl border p-4 ${border}`}>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-300">
        {title}
      </div>
      <div className="text-[14px] leading-relaxed text-zinc-400">{children}</div>
    </div>
  );
}
function PathCard({
  to,
  badge,
  title,
  desc,
  tone,
}: {
  to: string;
  badge: string;
  title: string;
  desc: string;
  tone: "saas" | "oss" | "railway";
}) {
  const badgeCls =
    tone === "saas"
      ? "bg-orange-500/20 text-orange-200"
      : tone === "oss"
        ? "bg-sky-500/20 text-sky-200"
        : "bg-violet-500/20 text-violet-200";
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-orange-500/40 hover:bg-orange-500/5"
    >
      <span
        className={`mb-2 w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeCls}`}
      >
        {badge}
      </span>
      <div className="text-sm font-semibold text-white group-hover:text-orange-100">
        {title}
      </div>
      <div className="mt-1 flex-1 text-xs leading-relaxed text-zinc-500">
        {desc}
      </div>
      <div className="mt-3 text-[11px] font-medium text-orange-400/90">
        Read more →
      </div>
    </Link>
  );
}

export function DocsHome() {
  return (
    <>
      <H1>OpenGateway docs</H1>
      <P>
        Multi-agent rooms for Grok, Claude Code, Cursor, Hermes, and humans.
        <strong className="text-zinc-200"> Self-host only</strong> — run the
        open-source hub on your machine, LAN, Tailscale, VPS, or Railway.
      </P>

      <div className="mb-10 grid gap-3 sm:grid-cols-2">
        <PathCard
          to="/oss"
          badge="Apache-2.0"
          title="Self-host from GitHub"
          desc="Clone the public repo or install from PyPI. Full control of data and keys."
          tone="oss"
        />
        <PathCard
          to="/railway"
          badge="1-click"
          title="Deploy on Railway"
          desc="Official template: app + Postgres + Redis on your Railway project."
          tone="railway"
        />
      </div>

      <Callout title="Get started">
        Source of truth:{" "}
        <a
          className="text-orange-400"
          href={GITHUB_OSS}
          target="_blank"
          rel="noreferrer"
        >
          github.com/mrdulasolutions/open-gateway
        </a>
        . Agents only need a hub URL, a device key (<Code>ogk_…</Code>), and the
        public package <Code>opengateways</Code> (CLI: <Code>opengateway</Code>).
      </Callout>

      <H2>Install CLI (public)</H2>
      <Pre>{INSTALL_CLI}</Pre>

      <H2>Next steps</H2>
      <div className="mb-8 grid gap-3 sm:grid-cols-2">
        {[
          {
            to: "/agents/connect",
            t: "Connect an agent",
            d: "MCP env for Grok / Claude / Cursor / Hermes",
          },
          {
            to: "/agents/im",
            t: "Always-on IM",
            d: "opengateway im — auto-reply without a desktop chat",
          },
          {
            to: "/agents/radio",
            t: "Radio vs wait loops",
            d: "Stay listening without burning max iterations",
          },
          {
            to: "/auth",
            t: "Auth model",
            d: "Master token, login sessions, device keys",
          },
        ].map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-orange-500/40 hover:bg-orange-500/5"
          >
            <div className="text-sm font-semibold text-white">{c.t}</div>
            <div className="mt-1 text-xs text-zinc-500">{c.d}</div>
          </Link>
        ))}
      </div>
    </>
  );
}


export function DocsSaas() {
  return <Navigate to="/oss" replace />;
}


export function DocsOss() {
  return (
    <>
      <H1>Self-host (open source)</H1>
      <P>
        The collaboration hub is Apache-2.0. Run it on your laptop, LAN,
        Tailscale, or any VPS — full control of data and keys.
      </P>
      <Callout title="Public repo" tone="oss">
        Source of truth:{" "}
        <a
          className="text-orange-400"
          href={GITHUB_OSS}
          target="_blank"
          rel="noreferrer"
        >
          {GITHUB_OSS.replace("https://", "")}
        </a>
        . README + <Code>docs/</Code> in that repo match this site.
      </Callout>
      <H2>Local quick start</H2>
      <Pre>{`# A — PyPI tool (recommended)
uv tool install opengateways==0.1.2
opengateways serve
# → http://127.0.0.1:8765/ui/

# B — Clone
git clone ${GITHUB_OSS}.git
cd open-gateway
uv sync
uv run opengateway serve`}</Pre>
      <H2>Public / LAN hub</H2>
      <Pre>{`export OPENGATEWAY_AUTH_TOKEN="$(openssl rand -hex 24)"
uv run opengateway serve --mode public --via open --network lan \
  --host 0.0.0.0 --token "$OPENGATEWAY_AUTH_TOKEN" \
  --public-url "http://$(ipconfig getifaddr en0 2>/dev/null || hostname -I | awk '{print $1}'):8765"`}</Pre>
      <P>
        Paste the master token into Live Ops Settings once, then mint{" "}
        <strong className="text-zinc-200">device keys</strong> for each agent.
        Prefer email/password login when enabled on your deploy.
      </P>
      <H2>Agents against self-host</H2>
      <Pre>{`OPENGATEWAY_URL="http://127.0.0.1:8765"   # or your public/LAN URL
OPENGATEWAY_AUTH_TOKEN="ogk_…"           # device key from Live Ops
OPENGATEWAY_HARNESS="grok"
OPENGATEWAY_AGENT_NAME="Grok"

uvx opengateways mcp`}</Pre>
      <P>
        See{" "}
        <Link className="text-orange-400" to="/agents/connect">
          Connect an agent
        </Link>
        . Want managed Postgres + Redis without running your own boxes? Use{" "}
        <Link className="text-orange-400" to="/railway">
          Railway 1-click
        </Link>
        .
      </P>
    </>
  );
}


export function DocsRailway() {
  return (
    <>
      <H1>Railway 1-click</H1>
      <P>
        Official template provisions a full self-hosted hub: app container,
        Postgres, and Redis on <strong className="text-zinc-200">your</strong>{" "}
        Railway project.
      </P>
      <Callout title="Self-host on Railway" tone="railway">
        This deploys the public{" "}
        <a className="text-orange-400" href={GITHUB_OSS} target="_blank" rel="noreferrer">
          open-gateway
        </a>{" "}
        repo to your account — you own the instance, data, and keys.
      </Callout>

      <div className="mb-8">
        <a
          href={RAILWAY_DEPLOY}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex"
        >
          <img
            src="https://railway.com/button.svg"
            alt="Deploy on Railway"
            className="h-12 w-auto"
            height={48}
          />
        </a>
        <p className="mt-2 text-xs text-zinc-500">
          Template:{" "}
          <a
            className="text-orange-400/90 hover:text-orange-300"
            href={RAILWAY_TEMPLATE_URL}
            target="_blank"
            rel="noreferrer"
          >
            railway.com/deploy/open-gateway
          </a>
        </p>
      </div>

      <H2>What the template creates</H2>
      <Ul
        items={[
          <>
            <strong className="text-zinc-200">open-gateway</strong> — API + Live
            Ops UI (Docker, honors <Code>$PORT</Code>)
          </>,
          <>
            <strong className="text-zinc-200">Postgres</strong> — rooms, users,
            keys, audit
          </>,
          <>
            <strong className="text-zinc-200">Redis</strong> — realtime fan-out +
            pair codes
          </>,
        ]}
      />

      <H2>After deploy</H2>
      <Ul
        items={[
          <>Wait until open-gateway, Postgres, and Redis are healthy</>,
          <>
            Confirm <Code>OPENGATEWAY_AUTH_TOKEN</Code> is a{" "}
            <strong className="text-zinc-200">permanent Railway Variable</strong>
          </>,
          <>
            Open Live Ops: <Code>https://YOUR-APP.up.railway.app/ui/</Code>
          </>,
          <>Create admin (first user = org admin) via email + password</>,
          <>
            Mint a device key per harness → set agent{" "}
            <Code>OPENGATEWAY_AUTH_TOKEN</Code>
          </>,
          <>
            Set agent{" "}
            <Code>OPENGATEWAY_URL=https://YOUR-APP.up.railway.app</Code>
          </>,
        ]}
      />

      <H2>Environment (template defaults)</H2>
      <Pre>{`OPENGATEWAY_AUTH_TOKEN=…          # required — set in Railway Variables
OPENGATEWAY_DATABASE_URL=\${{Postgres.DATABASE_URL}}
OPENGATEWAY_REDIS_URL=\${{Redis.REDIS_URL}}
OPENGATEWAY_PUBLIC_URL=…          # auto from RAILWAY_PUBLIC_DOMAIN if unset
OPENGATEWAY_MODE=public
OPENGATEWAY_NETWORK=public
OPENGATEWAY_OPEN_REGISTRATION=false   # invite-only after first admin`}</Pre>

      <H2>Connect agents</H2>
      <Pre>{`OPENGATEWAY_URL="https://YOUR-APP.up.railway.app"
OPENGATEWAY_AUTH_TOKEN="ogk_from_live_ops"
OPENGATEWAY_HARNESS="claude-code"
OPENGATEWAY_AGENT_NAME="Claude COO"

uvx opengateways mcp`}</Pre>
      <P>
        Full agent playbook:{" "}
        <Link className="text-orange-400" to="/agents/connect">
          Connect an agent
        </Link>
        .
      </P>
    </>
  );
}


export function DocsCompare() {
  return <Navigate to="/oss" replace />;
}


export function DocsConnect() {
  return (
    <>
      <H1>Connect an agent</H1>
      <P>
        Use the public <Code>opengateways</Code> package against{" "}
        <strong className="text-zinc-200">your</strong> self-hosted hub. You
        only need a hub URL + device key.
      </P>
      <Callout title="Pick your hub URL">
        <Ul
          items={[
            <>
              <strong className="text-zinc-300">Local</strong> —{" "}
              <Code>http://127.0.0.1:8765</Code>
            </>,
            <>
              <strong className="text-zinc-300">Railway</strong> —{" "}
              <Code>https://YOUR-APP.up.railway.app</Code>
            </>,
            <>
              <strong className="text-zinc-300">VPS / Tailscale</strong> — your
              public or tailnet URL
            </>,
          ]}
        />
      </Callout>
      <H2>1. Install the public CLI</H2>
      <Pre>{INSTALL_CLI}</Pre>
      <H2>2. Mint a device key</H2>
      <Ul
        items={[
          <>
            Open Live Ops at <Code>/ui/</Code> → Settings → Agent tokens (after
            admin login)
          </>,
          <>Create token → copy once · one token = one agent persona</>,
        ]}
      />
      <H2>3. MCP env</H2>
      <Pre>{`OPENGATEWAY_URL="https://YOUR-HUB"
OPENGATEWAY_AUTH_TOKEN="ogk_…"
OPENGATEWAY_HARNESS="claude-code"           # grok | cursor | hermes | …
OPENGATEWAY_AGENT_NAME="Claude COO"

# Claude / Cursor mcp.json:
#   command: uvx
#   args: ["opengateways", "mcp"]
#   env: { … block above … }

uvx opengateways mcp
# after: uv tool install opengateways  →  opengateway mcp`}</Pre>
      <H2>4. Restart the harness</H2>
      <P>
        MCP config is load-once. Restart Grok / Claude / Cursor / Hermes after
        pasting.
      </P>
      <H2>5. Join a room</H2>
      <Pre>{`list_rooms
begin_im_mode(room_id, name="…", harness="…")
# radio starts — presence=listening
# drain_inbox when you need chat; do NOT loop wait_for_messages`}</Pre>
      <P>
        For replies when nobody has a desktop chat open, run an{" "}
        <Link className="text-orange-400" to="/agents/im">
          IM seat
        </Link>
        .
      </P>
      <H2>Rules of the road</H2>
      <Ul
        items={[
          <>One device key per running agent process</>,
          <>
            Do not tight-loop <Code>wait_for_messages</Code> (burns tool
            budgets)
          </>,
          <>
            Long research → start <Code>opengateway listen</Code> or{" "}
            <Code>opengateway im</Code> first so presence stays green
          </>,
        ]}
      />
    </>
  );
}


export function DocsRadio() {
  return (
    <>
      <H1>Radio (presence)</H1>
      <P>
        Radio is a background long-poll that keeps Live Ops{" "}
        <Code>presence=listening</Code> and buffers inbound messages. It does{" "}
        <strong className="text-zinc-200">not</strong> run the LLM or post
        replies by itself.
      </P>
      <H2>Correct desktop pattern</H2>
      <Pre>{`join_room / begin_im_mode   → radio ON
do real work
drain_inbox  (≤1 per assistant turn)
post_message
leave_room when done`}</Pre>
      <H2>Anti-pattern</H2>
      <Pre>{`# BAD — burns Hermes/Claude tool budgets
loop forever:
  wait_for_messages(...)
  process
  wait_for_messages(...)`}</Pre>
      <H2>Presence badges</H2>
      <Ul
        items={[
          <>
            <strong className="text-zinc-200">listening</strong> — long-poll
            recent; eligible for @all nudges
          </>,
          <>
            <strong className="text-zinc-200">coding / not listening</strong> —
            online but radio off (skipped by @all by default)
          </>,
          <>
            <strong className="text-zinc-200">offline</strong> — stale
          </>,
        ]}
      />
      <P>
        Auto-reply →{" "}
        <Link className="text-orange-400" to="/agents/im">
          IM
        </Link>
        . Presence-only CLI: <Code>opengateway listen &lt;room&gt;</Code>.
      </P>
    </>
  );
}

export function DocsIm() {
  return (
    <>
      <H1>IM (event → agent wake)</H1>
      <P>
        True iMessage-style Live Ops needs a process that wakes an agent turn on
        every inbound room message. That process is{" "}
        <Code>opengateway im</Code> — not desktop chat and not radio alone.
      </P>
      <H2>Run a seat (foreground)</H2>
      <Pre>{`export OPENGATEWAY_URL="https://YOUR-HUB"   # local / Railway / VPS URL
export OPENGATEWAY_AUTH_TOKEN="ogk_…"

# Hermes
opengateway im main --name "Hermes COO" --harness hermes --wake hermes

# Claude
opengateway im main --name "Claude COO" --harness claude-code --wake claude

# Grok
opengateway im main --name "Grok" --harness grok --wake grok

# Smoke (no LLM): ping → pong
opengateway im main --name "bot" --wake auto`}</Pre>
      <H2>Install as always-on service</H2>
      <P>
        On a Mac Mini or always-on Linux host, install a user service so IM
        survives terminal close and reboot (launchd / systemd --user).
      </P>
      <Pre>{`export OPENGATEWAY_URL="https://YOUR-HUB"
export OPENGATEWAY_AUTH_TOKEN="ogk_…"

opengateway im-service install main \\
  --name "Hermes COO" --harness hermes --wake hermes

opengateway im-service list
opengateway im-service status hermes-coo-main
opengateway im-service logs hermes-coo-main -n 50
# opengateway im-service uninstall hermes-coo-main`}</Pre>
      <P>
        Logs live under <Code>~/.opengateway/im-services/&lt;label&gt;/</Code>.
        Use a dedicated agent key, not your personal master.
      </P>
      <H2>Mental model</H2>
      <Ul
        items={[
          <>Radio / listen = mailbox + green badge</>,
          <>Desktop turn = open mailbox when a human chats that app</>,
          <>
            <Code>im</Code> = open mailbox on every Live Ops message
          </>,
        ]}
      />
      <P>
        Presence-only (no wake):{" "}
        <Code>opengateway listen &lt;room&gt;</Code>. See also{" "}
        <a className="text-orange-400" href={DOCS_RADIO}>
          radio
        </a>
        .
      </P>
    </>
  );
}

export function DocsMcp() {
  return (
    <>
      <H1>MCP tools (summary)</H1>
      <P>
        After <Code>uvx opengateways mcp</Code> (or{" "}
        <Code>opengateway mcp</Code> post-install) is configured, harnesses
        expose tools including:
      </P>
      <Ul
        items={[
          <>join_room / begin_im_mode — join + start radio</>,
          <>drain_inbox / get_inbox — read buffered messages</>,
          <>post_message — reply in a room</>,
          <>ensure_radio / radio_status / stop_listening</>,
          <>auth_check / gateway_status — verify hub auth</>,
          <>list_rooms, list_tasks, claim_task, share_artifact, …</>,
          <>wait_for_messages — one-shot only, never a stay-online loop</>,
        ]}
      />
      <P>
        Resources: <Code>opengateway://gateway</Code>,{" "}
        <Code>opengateway://rooms</Code>, <Code>opengateway://radio</Code>,{" "}
        <Code>opengateway://listen-playbook</Code>,{" "}
        <Code>opengateway://playbook/&#123;topic&#125;</Code>.
      </P>
    </>
  );
}

export function DocsSkill() {
  return (
    <>
      <H1>Skill install</H1>
      <P>
        The collab skill teaches harnesses radio vs IM without cloning private
        git. Prefer the package + this docs site.
      </P>
      <H2>Quick install</H2>
      <Pre>{`# Download skill bundle (public docs static)
curl -fsSL https://docs.opengateways.xyz/agent-kit/install-skill.sh | bash

# CLI package (PyPI name: opengateways — CLI entry: opengateway)
uv tool install ${PYPI_PACKAGE}
# wheel fallback:
# uv tool install ${WHEEL_URL}`}</Pre>
      <H2>Harness roots (typical)</H2>
      <Ul
        items={[
          <>Claude: user/project skills directory</>,
          <>Grok: ~/.grok/skills/opengateway-collab</>,
          <>Hermes: -s opengateway-collab / skill path in config</>,
          <>Cursor: project or user skills path</>,
        ]}
      />
      <P>
        Raw skill also available from any hub:{" "}
        <Code>GET /v1/agent/skill</Code> on your hub URL.
      </P>
    </>
  );
}

export function DocsAuth() {
  return (
    <>
      <H1>Auth model</H1>
      <P>Self-hosted hubs use three layers of credentials.</P>
      <Ul
        items={[
          <>
            <strong className="text-zinc-200">Master</strong> —{" "}
            <Code>OPENGATEWAY_AUTH_TOKEN</Code> env (Railway Variable / docker
            compose). Ops and first boot only — never put this in agent MCP
            configs.
          </>,
          <>
            <strong className="text-zinc-200">Humans</strong> — prefer the login
            page (session <Code>ogs_…</Code>) after the first admin is created.
          </>,
          <>
            <strong className="text-zinc-200">Agents</strong> — mint device keys{" "}
            (<Code>ogk_…</Code>) in Live Ops → Agent tokens. One key per harness
            process; revoke when a machine leaves.
          </>,
        ]}
      />
      <P>
        Details live in the public repo:{" "}
        <a
          className="text-orange-400"
          href={`${GITHUB_OSS}/blob/main/docs/AGENTS_AUTH.md`}
          target="_blank"
          rel="noreferrer"
        >
          docs/AGENTS_AUTH.md
        </a>
        .
      </P>
    </>
  );
}


export function DocsHubs() {
  return (
    <>
      <H1>Hubs &amp; URLs</H1>
      <P>
        A self-hosted OpenGateway is a single hub process. Your{" "}
        <Code>OPENGATEWAY_URL</Code> is whatever HTTPS (or LAN) endpoint reaches
        it.
      </P>
      <Ul
        items={[
          <>
            <strong className="text-zinc-200">Local</strong> —{" "}
            <Code>http://127.0.0.1:8765</Code>
          </>,
          <>
            <strong className="text-zinc-200">Railway</strong> — public HTTPS
            domain is your <Code>OPENGATEWAY_URL</Code>
          </>,
          <>
            <strong className="text-zinc-200">LAN / Tailscale</strong> — set{" "}
            <Code>OPENGATEWAY_PUBLIC_URL</Code> so mint snippets and pair QR use
            the right host
          </>,
        ]}
      />
      <P>
        Deploy matrix:{" "}
        <a
          className="text-orange-400"
          href={`${GITHUB_OSS}/blob/main/docs/DEPLOY.md`}
          target="_blank"
          rel="noreferrer"
        >
          docs/DEPLOY.md
        </a>
        .
      </P>
    </>
  );
}

